'use client';

import { observable } from 'mobx';
import { detach, isAlive, t } from 'mobx-state-tree';

import * as M from './properties';
import { type IObjectPropertyLike, type IProperty } from './propertyTypes';

import type { IAssetFile, IEditableObject } from '@/editor/store/models';
import type { Instance } from 'mobx-state-tree';

import { getUnifiedPropertyMeta } from '@/app/editor/core/editorClassMeta';

export interface IPropertySource {
  editable: IEditableObject | IAssetFile | null;
  target: any;
}

export const PropertyStore = t
  .model('PropertyStore', {
    id: t.identifier,
    propertyTree: t.maybe(M.ObjectProperty),
  })
  .volatile(() => ({
    sources: [] as IPropertySource[],
    _propertyCache: new Map<string, IProperty>(),
    _expansionState: observable.map<string, boolean>(),
  }))
  .views(self => ({
    isExpanded(key: string, defaultExpanded = true): boolean {
      const stored = self._expansionState.get(key);
      return stored !== undefined ? stored : defaultExpanded;
    },
    get editables(): (IEditableObject | IAssetFile)[] {
      return self.sources.map(source => source.editable).filter(Boolean) as (IEditableObject | IAssetFile)[];
    },
    get targets() {
      return self.sources.map(source => source.target).filter(Boolean);
    },
    get excludedPaths(): string[] {
      return [...this.properties.keys(), ...EXCLUDED_PATHS];
    },
    getPropertyMeta(path: string) {
      const target = this.targets[0];
      if (!target) return undefined;
      return getUnifiedPropertyMeta(target, path);
    },
    getVisiblePropertyKeys(searchTerm?: string): Set<string> {
      const visibleKeys = new Set<string>();
      const normalizedSearchTerm = searchTerm?.trim().toLowerCase();
      if (!normalizedSearchTerm || !self.propertyTree) {
        return visibleKeys;
      }

      const matchesSearch = (label: string) => label.toLowerCase().includes(normalizedSearchTerm);

      /** Recursively marks a property and all its descendants visible (used when a container label matches). */
      const markAllVisible = (property: IProperty) => {
        visibleKeys.add(property.key);
        if (M.ObjectProperty.is(property)) {
          property.orderedProperties.forEach(markAllVisible);
        } else if (M.ArrayProperty.is(property)) {
          property.properties.forEach(markAllVisible);
        }
      };

      const visitProperty = (property: IProperty): boolean => {
        const selfMatches = matchesSearch(property.label);

        if (selfMatches) {
          markAllVisible(property);
          return true;
        }

        let hasVisibleChild = false;
        if (M.ObjectProperty.is(property)) {
          for (const child of property.orderedProperties) {
            hasVisibleChild = visitProperty(child) || hasVisibleChild;
          }
        } else if (M.ArrayProperty.is(property)) {
          for (const child of property.properties) {
            hasVisibleChild = visitProperty(child) || hasVisibleChild;
          }
        }

        if (hasVisibleChild) {
          visibleKeys.add(property.key);
        }
        return hasVisibleChild;
      };

      visitProperty(self.propertyTree);
      return visibleKeys;
    },
    /** Returns a map of property paths to property keys */
    get properties() {
      const properties = new Map<string, string>();
      const addProperties = (property: IProperty) => {
        properties.set(property.fullPath, property.key);
        if (M.ObjectProperty.is(property) || M.ArrayProperty.is(property)) {
          property.properties.forEach(addProperties);
        }
      };
      self.propertyTree?.properties.forEach(addProperties);
      return properties;
    },
  }))
  .actions(self => ({
    beforeDestroy() {
      self._propertyCache.clear();
    },
    setExpanded(key: string, expanded: boolean) {
      self._expansionState.set(key, expanded);
    },
    expandAll() {
      if (!self.propertyTree) return;
      const visit = (property: IProperty) => {
        if (M.ObjectProperty.is(property)) {
          if (!property.isClassSection) {
            self._expansionState.set(property.key, true);
          }
          property.orderedProperties.forEach(visit);
        } else if (M.ArrayProperty.is(property)) {
          property.properties.forEach(visit);
        }
      };
      self.propertyTree.orderedProperties.forEach(visit);
    },
    collapseAll() {
      if (!self.propertyTree) return;
      const visit = (property: IProperty) => {
        if (M.ObjectProperty.is(property)) {
          if (!property.isClassSection) {
            self._expansionState.set(property.key, false);
          }
          property.orderedProperties.forEach(visit);
        } else if (M.ArrayProperty.is(property)) {
          property.properties.forEach(visit);
        }
      };
      self.propertyTree.orderedProperties.forEach(visit);
    },
    setSources(sources: IPropertySource[]) {
      self.sources = sources;
    },
    setPropertyTree(property: IObjectPropertyLike | null | undefined) {
      if (self.propertyTree && isAlive(self.propertyTree)) {
        detach(self.propertyTree);
      }
      self.propertyTree = property ? property as Instance<typeof M.ObjectProperty> : undefined;
      self._propertyCache.clear(); // Clear cache when property tree changes
    },
    findPropertyByPath(path: string): IProperty | null {
      // Check cache first
      if (self._propertyCache.has(path)) {
        return self._propertyCache.get(path)!;
      }

      if (!self.propertyTree) return null;

      // Recursively search through property tree, matching by fullPath or bare path
      const findInProperties = (properties: IProperty[], targetPath: string): IProperty | null => {
        for (const property of properties) {
          if (property.fullPath === targetPath || property.path === targetPath) {
            return property;
          }

          // Search in nested properties
          if (M.ObjectProperty.is(property)) {
            // ObjectProperty has properties as a Map
            const nestedProperties = Array.from(property.properties.values());
            const found = findInProperties(nestedProperties, targetPath);
            if (found) return found;
          } else if (M.ArrayProperty.is(property)) {
            // ArrayProperty has properties as an array
            const found = findInProperties(property.properties, targetPath);
            if (found) return found;
          }
        }
        return null;
      };

      // Start with the root property tree (ObjectProperty with Map)
      const rootProperties = Array.from(self.propertyTree.properties.values());
      const result = findInProperties(rootProperties, path);

      // Cache the result (including null results to avoid repeated searches)
      if (result !== null) {
        self._propertyCache.set(path, result);
      }

      return result;
    },
    clearPropertyCache() {
      self._propertyCache.clear();
    },
    setPropertyValue(path: string, value: any, fromPanel = false, initial = false) {
      const property = this.findPropertyByPath(path);
      if (property && 'setValue' in property) {
        // console.log('setPropertyValue', path, value, getSnapshot(property), fromPanel, initial);
        (property as any).setValue(value, fromPanel, initial);
        return true;
      }
      return false;
    }
  }));

export interface IPropertyStore extends Instance<typeof PropertyStore> { }

const EXCLUDED_PATHS = [
  'options.position',
  'options.rotation',
  'options.scale',
  'options.visible',
  'options.castShadow',
  'options.receiveShadow',
  'options.frustumCulled',
  'options.renderOrder',
  'options.up',
  'options.userData',
  'options.rootComponent',
  'options.sceneComponents',
  'options.components'
];
